import { useMutation, useQuery } from "@apollo/client"
import { TiptapDisplay } from "@skyltmax/ds9"
import { type Extensions } from "@tiptap/core"
import Document from "@tiptap/extension-document"
import Link from "@tiptap/extension-link"
import Placeholder from "@tiptap/extension-placeholder"
import Subscript from "@tiptap/extension-subscript"
import Superscript from "@tiptap/extension-superscript"
import TextAlign from "@tiptap/extension-text-align"
import Typography from "@tiptap/extension-typography"
import Underline from "@tiptap/extension-underline"
import StarterKit from "@tiptap/starter-kit"
import cn from "classnames"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useForm, type SubmitHandler } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { graphql, type FragmentType, getFragment } from "@/api"
import StaticTiptapDisplay from "@/client/components/static_tiptap_display"
import Checkbox from "@/common/components/checkbox"
import FormForm from "@/common/components/form_form"
import FormInput from "@/common/components/form_input"
import FormTextArea from "@/common/components/form_textarea"
import Modal from "@/common/components/modal"
import StaticContent from "@/common/components/static_content"
import { CheckmarkFill, PaperPlaneFill } from "@/skylticons"
import Uploader from "@/uploader/components/uploader"

const HeadingDocument = Document.extend({ content: "heading" })
const ParagraphDocument = Document.extend({ content: "paragraph+" })

export const quoteFormHeadingStack: Extensions = [
  StarterKit.configure({
    document: false,
    bold: false,
    italic: false,
    heading: {
      levels: [3],
      HTMLAttributes: {
        class: "mb-4",
      },
    },
  }),
  HeadingDocument,
  Placeholder,
  Subscript,
  Superscript,
  TextAlign.configure({ types: ["heading"] }),
  Typography.configure({
    openDoubleQuote: false,
    closeDoubleQuote: false,
    openSingleQuote: false,
    closeSingleQuote: false,
  }),
]

export const quoteSuccessHeadingStack: Extensions = [
  StarterKit.configure({
    document: false,
    bold: false,
    heading: {
      levels: [4],
      HTMLAttributes: {
        class: "mb-1",
      },
    },
  }),
  HeadingDocument,
  Placeholder,
  Underline,
  Link.configure({ openOnClick: false, HTMLAttributes: { target: "_self", rel: "noopener" } }),
  Subscript,
  Superscript,
  TextAlign.configure({ types: ["heading"] }),
  Typography.configure({
    openDoubleQuote: false,
    closeDoubleQuote: false,
    openSingleQuote: false,
    closeSingleQuote: false,
  }),
]

export const quoteSuccessParagraphStack: Extensions = [
  StarterKit.configure({
    document: false,
    paragraph: {
      HTMLAttributes: {
        class: "mb-4 paragraph-xs",
      },
    },
  }),
  ParagraphDocument,
  Placeholder,
  Underline,
  Link.configure({ openOnClick: false, HTMLAttributes: { target: "_self", rel: "noopener" } }),
  Subscript,
  Superscript,
  TextAlign.configure({ types: ["paragraph"] }),
  Typography.configure({
    openDoubleQuote: false,
    closeDoubleQuote: false,
    openSingleQuote: false,
    closeSingleQuote: false,
  }),
]

export const quoteSuccessSubheadingStack: Extensions = [
  StarterKit.configure({
    document: false,
    bold: false,
    heading: {
      levels: [5],
      HTMLAttributes: {
        class: "mb-3",
      },
    },
  }),
  HeadingDocument,
  Placeholder,
  Underline,
  Link.configure({ openOnClick: false, HTMLAttributes: { target: "_self", rel: "noopener" } }),
  Subscript,
  Superscript,
  TextAlign.configure({ types: ["heading"] }),
  Typography.configure({
    openDoubleQuote: false,
    closeDoubleQuote: false,
    openSingleQuote: false,
    closeSingleQuote: false,
  }),
]

export const quoteSuccessSubdescriptionStack: Extensions = [
  StarterKit.configure({
    document: false,
    paragraph: {
      HTMLAttributes: {
        class: "mb-0 paragraph-xs",
      },
    },
  }),
  ParagraphDocument,
  Placeholder,
  Underline,
  Link.configure({ openOnClick: false, HTMLAttributes: { target: "_self", rel: "noopener" } }),
  Subscript,
  Superscript,
  TextAlign.configure({ types: ["paragraph"] }),
  Typography.configure({
    openDoubleQuote: false,
    closeDoubleQuote: false,
    openSingleQuote: false,
    closeSingleQuote: false,
  }),
]

export const QuoteFormFragment = graphql(`
  fragment FrontpagesQuoteForm on FrontpagesQuoteForm {
    id
    heading
    emailLabel
    nameLabel
    phoneLabel
    descriptionLabel
    descriptionHelp
    fileLabel
    buttonLabel
    successHeading
    successDescription
    successSubheading
    successSubdescription
  }
`)

const snippetDoc = graphql(`
  query FrontpagesQuoteFormSnippet {
    snippetContent(tag: "information_notice_contact")
  }
`)

const quoteMutationDoc = graphql(`
  mutation QuoteSubmit($email: String!, $name: String, $phone: String, $description: String!, $files: [JSON!]) {
    quoteSubmit(email: $email, name: $name, phone: $phone, description: $description, files: $files) {
      success

      errors {
        path
        message
      }
    }
  }
`)

export interface QuoteInputs {
  email: string
  name?: string
  phone?: string
  description: string
  files?: any[]
}

interface QuoteFormProps {
  email?: string
  open: boolean
  setOpen: (open: boolean) => void
  form: FragmentType<typeof QuoteFormFragment>
}

const QuoteForm: React.FC<QuoteFormProps> = ({ form: formFragment, email, open, setOpen }) => {
  const { t } = useTranslation()
  const form = getFragment(QuoteFormFragment, formFragment)

  // we load the snippet client side so we don't have to consider it in the frontpage fragment cache
  const snippetResults = useQuery(snippetDoc, { ssr: false })
  const snippet = snippetResults.data?.snippetContent

  const formMethods = useForm<QuoteInputs>()
  const { formState, handleSubmit, reset, register } = formMethods

  const [successOpen, setSuccessOpen] = useState(false)
  const successTimer = useRef<number>()

  const [showUploader, setShowUploader] = useState(false)

  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      if (successTimer.current) {
        clearTimeout(successTimer.current)
        successTimer.current = undefined
      }

      reset({ email })
      setShowUploader(false)
      setSuccessOpen(false)
    }
  }, [reset, open, email])

  useEffect(() => {
    if (successOpen && !successTimer.current) {
      successTimer.current = window.setTimeout(() => {
        setSuccessOpen(false)

        successTimer.current = undefined
      }, 10000)
    }
  }, [successOpen])

  const [mutation] = useMutation(quoteMutationDoc)

  const submit: SubmitHandler<QuoteInputs> = useCallback(
    async (data, e) => {
      e?.preventDefault()

      try {
        const result = await mutation({ variables: { ...data, files: data.files?.map(f => JSON.parse(f || "{}")) } })
        const mutationResult = result.data?.quoteSubmit

        if (mutationResult) {
          const errors = mutationResult.errors

          if (errors && errors.length > 0) {
            const error = errors[0]

            if (error.path && error.path.length > 0) {
              alert(`${error.path[0]}: ${error.message}`)
            }

            return
          }

          if (mutationResult.success) {
            setOpen(false)
            setSuccessOpen(true)
          }
        }
      } catch (e) {
        console.error(e)
      }
    },
    [mutation, setOpen]
  )

  return (
    <>
      <Modal width="600px" className="quote-modal" open={successOpen} setOpen={setSuccessOpen} padding="small">
        <Modal.Body className="success flex gap-3">
          <CheckmarkFill className="h-12 w-12 text-success" />
          <StaticContent>
            <div className="pr-14">
              <TiptapDisplay extensions={quoteSuccessHeadingStack} json={form.successHeading} />
              <TiptapDisplay extensions={quoteSuccessParagraphStack} json={form.successDescription} />
            </div>
            {form.successSubheading && (
              <TiptapDisplay extensions={quoteSuccessSubheadingStack} json={form.successSubheading} />
            )}
            {form.successSubdescription && (
              <TiptapDisplay extensions={quoteSuccessSubdescriptionStack} json={form.successSubdescription} />
            )}
          </StaticContent>
          <Modal.CloseButton />
        </Modal.Body>
      </Modal>

      <Modal width="600px" open={open} setOpen={setOpen} initialFocus={inputRef}>
        <Modal.Body>
          <Modal.Title>
            <StaticTiptapDisplay extensions={quoteFormHeadingStack} json={form.heading} />
          </Modal.Title>
          <FormForm onSubmit={handleSubmit(submit)} formMethods={formMethods}>
            <FormInput
              name="email"
              label={`* ${form.emailLabel}`}
              rules={{
                required: true,
                minLength: 6,
                maxLength: 254,
                pattern: {
                  value: /^([^@\s]+)@((?:[-a-z0-9]+\.)+[a-z]{2,})$/i,
                  message: t("errors.messages_invalid"),
                },
              }}
              ref={email ? null : inputRef}
            />
            <FormInput name="name" label={form.nameLabel} ref={email ? inputRef : null} />
            <FormInput name="phone" label={form.phoneLabel} />
            <FormTextArea
              name="description"
              label={`* ${form.descriptionLabel}`}
              help={form.descriptionHelp}
              rules={{ required: true }}
            />

            <Checkbox
              name="upload_image"
              id="quote_upload_image"
              label={form.fileLabel}
              onChange={() => setShowUploader(v => !v)}
              checked={showUploader}
              className="mb-4"
            />

            {showUploader && (
              <div className="mb-4">
                <Uploader name="files" formRegister={register} />
              </div>
            )}

            <button
              className="button button-primary full-width icon-right"
              type="submit"
              disabled={snippetResults.loading || formState.isSubmitting}
            >
              {form.buttonLabel}
              <PaperPlaneFill />
            </button>
            <p
              className={cn("paragraph-2xs mb-0", { skeleton: snippetResults.loading })}
              dangerouslySetInnerHTML={{ __html: snippet || "text" }}
            ></p>
          </FormForm>
          <Modal.CloseButton />
        </Modal.Body>
      </Modal>
    </>
  )
}

export default QuoteForm
